import {
	iVoiceRemoteUser,
	iVoiceSocketServerContextType,
	VoiceSocketServerContext,
} from "./useVoiceSocketServerContextProvider";
import React, {
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { API } from "../API/api";
import useGlobalSettings from "./useGlobalSettings";

type VoiceUserMessage = {
	username?: string;
};

type VoiceSocketMessage =
	| {
	type: "joined";
	username: string;
	room: string;
}
	| {
	type: "users";
	users: VoiceUserMessage[];
}
	| {
	type: "speaking";
	username: string;
}
	| {
	type: "power";
	username: string;
	volume: number;
}
	| {
	type: string;
	[key: string]: unknown;
};

type RemoteAudioUser = {
	username: string;
	audio: HTMLAudioElement | null;
	mediaSource: MediaSource | null;
	sourceBuffer: SourceBuffer | null;
	pendingAudioChunks: ArrayBuffer[];
	mediaSourceReady: boolean;
	volume: number;
	powerTimeout: ReturnType<typeof setTimeout> | null;
	speakingTimeout: ReturnType<typeof setTimeout> | null;
};

const getSupportedMimeType = () => {
	const candidates = [
		"audio/webm;codecs=opus",
		"audio/webm",
		"audio/ogg;codecs=opus",
		"audio/ogg",
	];

	for (const type of candidates) {
		if (MediaRecorder.isTypeSupported(type) && MediaSource.isTypeSupported(type)) {
			return type;
		}
	}

	for (const type of candidates) {
		if (MediaRecorder.isTypeSupported(type)) {
			return type;
		}
	}

	return "";
};

const calculateChunkPower = (arrayBuffer: ArrayBuffer) => {
	return Math.min(100, Math.round((arrayBuffer.byteLength / 6000) * 100));
};

const calculateMicrophoneVolume = (
	analyser: AnalyserNode,
	dataArray: Uint8Array
) => {
	analyser.getByteTimeDomainData(dataArray);

	let sumSquares = 0;

	for( let index = 0; index < dataArray.length; index++ ){
		const normalized = (dataArray[index] - 128) / 128;
		sumSquares += normalized * normalized;
	}

	const rms = Math.sqrt(sumSquares / dataArray.length);

	return Math.min(100, Math.round(rms * 320));
};

export const VoiceSocketServer = ({ children }: { children: ReactNode }) => {

	const { user } = useGlobalSettings()
	const [userName, setUserName] = useState<string>("Test");
	const [room, setRoom] = useState<string>("GLOBAL");

	const [connectedState, setConnectedState] = useState<boolean>(false);
	const [microphoneActive, setMicrophoneActive] = useState<boolean>(false);
	const [microphoneMuted, setMicrophoneMuted] = useState<boolean>(false);
	const [microphoneVolume, setMicrophoneVolume] = useState<number>(0);
	const [speakingUsername, setSpeakingUsername] = useState<string>("");
	const [users, setUsers] = useState<iVoiceRemoteUser[]>([]);

	const socket = useRef<WebSocket | null>(null);
	const localStream = useRef<MediaStream | null>(null);
	const mediaRecorder = useRef<MediaRecorder | null>(null);
	const selectedMimeType = useRef<string>("");
	const currentUsername = useRef<string>("");
	const lastSpeakingUsername = useRef<string>("");
	const speakingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const microphoneAudioContext = useRef<AudioContext | null>(null);
	const microphoneAnalyser = useRef<AnalyserNode | null>(null);
	const microphoneSource = useRef<MediaStreamAudioSourceNode | null>(null);
	const microphoneAnimationFrame = useRef<number | null>(null);
	const microphoneDataArray = useRef<Uint8Array | null>(null);
	const microphoneVolumeRef = useRef<number>(0);

	const remoteUsers = useRef<Map<string, RemoteAudioUser>>(new Map());

	const updateUserState = useCallback(
		(
			username: string,
			patch: Partial<Omit<iVoiceRemoteUser, "username">>
		) => {
			setUsers(prev => {
				const existingUser = prev.find(user => user.username === username);

				if (!existingUser) {
					return [
						...prev,
						{
							username,
							volume: 1,
							isSpeaking: false,
							audioPower: 0,
							...patch,
						},
					];
				}

				return prev.map(user =>
					user.username === username
						? {
							...user,
							...patch,
						}
						: user
				);
			});
		},
		[]
	);

	const createRemoteUser = useCallback(
		(username: string) => {
			const existingUser = remoteUsers.current.get(username);

			if (existingUser) {
				return existingUser;
			}

			const user: RemoteAudioUser = {
				username,
				audio: null,
				mediaSource: null,
				sourceBuffer: null,
				pendingAudioChunks: [],
				mediaSourceReady: false,
				volume: 1,
				powerTimeout: null,
				speakingTimeout: null,
			};

			remoteUsers.current.set(username, user);
			updateUserState(username, {
				volume: 1,
				isSpeaking: false,
				audioPower: 0,
			});

			return user;
		},
		[updateUserState]
	);

	const appendNextRemoteAudioChunk = useCallback((user: RemoteAudioUser) => {
		if (
			!user.mediaSourceReady ||
			!user.sourceBuffer ||
			user.sourceBuffer.updating ||
			user.pendingAudioChunks.length === 0
		) {
			return;
		}

		const chunk = user.pendingAudioChunks.shift();

		if (!chunk) {
			return;
		}

		try {
			user.sourceBuffer.appendBuffer(chunk);
		} catch (error) {
			console.error(
				`Audio-Chunk von ${user.username} konnte nicht angehängt werden:`,
				error
			);
		}
	}, []);

	const setupRemoteUserAudio = useCallback(
		(user: RemoteAudioUser) => {
			if (user.audio || !selectedMimeType.current) {
				return;
			}

			if (!MediaSource.isTypeSupported(selectedMimeType.current)) {
				console.warn(`MediaSource unterstützt ${selectedMimeType.current} nicht.`);
				return;
			}

			const audio = document.createElement("audio");
			audio.autoplay = true;
			audio.controls = false;
			audio.volume = user.volume;
			audio.style.display = "none";

			document.body.appendChild(audio);

			const mediaSource = new MediaSource();

			user.audio = audio;
			user.mediaSource = mediaSource;
			audio.src = URL.createObjectURL(mediaSource);

			mediaSource.addEventListener("sourceopen", () => {
				try {
					const sourceBuffer = mediaSource.addSourceBuffer(
						selectedMimeType.current
					);

					sourceBuffer.mode = "sequence";
					sourceBuffer.addEventListener("updateend", () =>
						appendNextRemoteAudioChunk(user)
					);

					user.sourceBuffer = sourceBuffer;
					user.mediaSourceReady = true;

					appendNextRemoteAudioChunk(user);

					audio.play().catch(error => {
						console.warn(
							`Autoplay für ${user.username} blockiert:`,
							error.message
						);
					});
				} catch (error) {
					console.error(
						`Remote-Audio für ${user.username} konnte nicht initialisiert werden:`,
						error
					);
				}
			});
		},
		[appendNextRemoteAudioChunk]
	);

	const updateRemotePowerIndicator = useCallback(
		(user: RemoteAudioUser, arrayBuffer: ArrayBuffer) => {
			const power = calculateChunkPower(arrayBuffer);

			updateUserState(user.username, {
				audioPower: power,
				isSpeaking: true,
			});

			if (user.powerTimeout) {
				clearTimeout(user.powerTimeout);
			}

			user.powerTimeout = setTimeout(() => {
				updateUserState(user.username, {
					audioPower: 0,
				});
			}, 250);

			if (user.speakingTimeout) {
				clearTimeout(user.speakingTimeout);
			}

			user.speakingTimeout = setTimeout(() => {
				updateUserState(user.username, {
					isSpeaking: false,
				});
			}, 800);
		},
		[updateUserState]
	);

	const appendRemoteAudioChunk = useCallback(
		(username: string, arrayBuffer: ArrayBuffer) => {
			if (!username || username === currentUsername.current) {
				return;
			}

			const user = createRemoteUser(username);

			setupRemoteUserAudio(user);

			user.pendingAudioChunks.push(arrayBuffer);
			updateRemotePowerIndicator(user, arrayBuffer);
			appendNextRemoteAudioChunk(user);
		},
		[
			appendNextRemoteAudioChunk,
			createRemoteUser,
			setupRemoteUserAudio,
			updateRemotePowerIndicator,
		]
	);

	const cleanupRemoteUser = useCallback(
		(user: RemoteAudioUser) => {
			if (user.powerTimeout) {
				clearTimeout(user.powerTimeout);
			}

			if (user.speakingTimeout) {
				clearTimeout(user.speakingTimeout);
			}

			if (user.audio) {
				user.audio.pause();

				if (user.audio.src) {
					URL.revokeObjectURL(user.audio.src);
				}

				user.audio.remove();
			}

			user.pendingAudioChunks = [];
			user.sourceBuffer = null;
			user.mediaSource = null;
			user.mediaSourceReady = false;
			user.audio = null;

			setUsers(prev => prev.filter(entry => entry.username !== user.username));
		},
		[]
	);

	const cleanupRemoteUsers = useCallback(() => {
		remoteUsers.current.forEach(user => {
			cleanupRemoteUser(user);
		});

		remoteUsers.current.clear();
		setUsers([]);
		lastSpeakingUsername.current = "";
		setSpeakingUsername("");
	}, [cleanupRemoteUser]);

	const renderUsers = useCallback(
		(incomingUsers: VoiceUserMessage[]) => {
			const userNames = Array.isArray(incomingUsers)
				? incomingUsers
					.map(user => user.username)
					.filter((username): username is string => Boolean(username))
				: [];

			remoteUsers.current.forEach((user, username) => {
				if (!userNames.includes(username)) {
					cleanupRemoteUser(user);
					remoteUsers.current.delete(username);
				}
			});

			setUsers(prev => {
				const currentUsers = userNames.map(username => {
					const existingUser = prev.find(user => user.username === username);

					createRemoteUser(username);

					return (
						existingUser ?? {
							username,
							volume: 1,
							isSpeaking: false,
							audioPower: 0,
						}
					);
				});

				return currentUsers;
			});
		},
		[cleanupRemoteUser, createRemoteUser]
	);

	const showSpeakingUser = useCallback(
		(username: string) => {
			lastSpeakingUsername.current = username || "";
			setSpeakingUsername(username || "");

			if (username) {
				const user = createRemoteUser(username);

				updateUserState(username, {
					isSpeaking: true,
				});

				if (user.speakingTimeout) {
					clearTimeout(user.speakingTimeout);
				}

				user.speakingTimeout = setTimeout(() => {
					updateUserState(username, {
						isSpeaking: false,
					});
				}, 800);
			}

			if (speakingTimeout.current) {
				clearTimeout(speakingTimeout.current);
			}

			speakingTimeout.current = setTimeout(() => {
				setSpeakingUsername("");
			}, 1500);
		},
		[createRemoteUser, updateUserState]
	);

	const handleTextMessage = useCallback(
		(text: string) => {
			try {
				const message = JSON.parse(text) as VoiceSocketMessage;

				if (message.type === "joined") {
					if (typeof message.username === "string") {
						currentUsername.current = message.username;
					}

					return;
				}

				if (message.type === "users") {
					if (Array.isArray(message.users)) {
						renderUsers(message.users);
					}

					return;
				}

				if (message.type === "speaking") {
					if (typeof message.username === "string") {
						showSpeakingUser(message.username);
					}

					return;
				}

				if (message.type === "power") {
					if (
						typeof message.username === "string" &&
						typeof message.volume === "number"
					) {
						updateUserState(message.username, {
							audioPower: Math.max(0, Math.min(100, message.volume)),
							isSpeaking: message.volume > 2,
						});
					}

					return;
				}

				console.warn("Unbekannte Voice-Nachricht:", message);
			} catch {
				console.warn("Textnachricht empfangen:", text);
			}
		},
		[renderUsers, showSpeakingUser, updateUserState]
	);

	const resetMicrophonePowerIndicator = useCallback(() => {
		setMicrophoneVolume(0);
		setMicrophoneActive(false);
	}, []);

	const stopMicrophonePowerIndicator = useCallback(() => {
		if (microphoneAnimationFrame.current) {
			cancelAnimationFrame(microphoneAnimationFrame.current);
			microphoneAnimationFrame.current = null;
		}

		if (microphoneSource.current) {
			try {
				microphoneSource.current.disconnect();
			} catch {
				// Ignore disconnect errors.
			}

			microphoneSource.current = null;
		}

		if (microphoneAudioContext.current) {
			microphoneAudioContext.current.close().catch(() => {
				// Ignore close errors.
			});

			microphoneAudioContext.current = null;
		}

		microphoneAnalyser.current = null;
		microphoneDataArray.current = null;

		resetMicrophonePowerIndicator();
	}, [resetMicrophonePowerIndicator]);

	const updateMicrophonePowerIndicator = useCallback(() => {
		if (!microphoneAnalyser.current || !microphoneDataArray.current) {
			return;
		}

		const volumePercent = calculateMicrophoneVolume(
			microphoneAnalyser.current,
			microphoneDataArray.current
		);

		microphoneVolumeRef.current = volumePercent;
		setMicrophoneVolume(volumePercent);

		microphoneAnimationFrame.current = requestAnimationFrame(
			updateMicrophonePowerIndicator
		);
	}, []);

	const startMicrophonePowerIndicator = useCallback(
		(stream: MediaStream) => {
			stopMicrophonePowerIndicator();

			const AudioContextClass =
				window.AudioContext ||
				(window as typeof window & { webkitAudioContext?: typeof AudioContext })
					.webkitAudioContext;

			if (!AudioContextClass) {
				console.warn("AudioContext wird von diesem Browser nicht unterstützt.");
				return;
			}

			const audioContext = new AudioContextClass();
			const analyser = audioContext.createAnalyser();

			analyser.fftSize = 1024;
			analyser.smoothingTimeConstant = 0.75;

			const source = audioContext.createMediaStreamSource(stream);
			source.connect(analyser);

			microphoneAudioContext.current = audioContext;
			microphoneAnalyser.current = analyser;
			microphoneSource.current = source;
			microphoneDataArray.current = new Uint8Array(analyser.fftSize);

			setMicrophoneActive(true);
			updateMicrophonePowerIndicator();
		},
		[stopMicrophonePowerIndicator, updateMicrophonePowerIndicator]
	);

	const cleanupRecording = useCallback(() => {
		if (
			mediaRecorder.current &&
			mediaRecorder.current.state !== "inactive"
		) {
			mediaRecorder.current.stop();
		}

		mediaRecorder.current = null;

		if (localStream.current) {
			for (const track of localStream.current.getTracks()) {
				track.stop();
			}
		}

		localStream.current = null;
		setMicrophoneMuted(false);

		stopMicrophonePowerIndicator();
	}, [stopMicrophonePowerIndicator]);

	const disconnect = useCallback(() => {
		cleanupRecording();
		cleanupRemoteUsers();

		if (socket.current) {
			socket.current.close();
			socket.current = null;
		}

		currentUsername.current = "";
		setConnectedState(false);
	}, [cleanupRecording, cleanupRemoteUsers]);

	const connect = useCallback(
		(nextRoom?: string, nextUserName?: string) => {
			const url = API.voiceSocket;
			const resolvedRoom = nextRoom?.trim() || room.trim();
			const resolvedUserName = nextUserName?.trim() || (user.name ?? "").trim();

			if (!url) {
				alert("Bitte WebSocket-URL eingeben.");
				return;
			}

			if (!/^[a-zA-Z0-9_-]{1,64}$/.test(resolvedRoom)) {
				alert("Der Raumname darf nur Buchstaben, Zahlen, _ und - enthalten.");
				return;
			}

			selectedMimeType.current = getSupportedMimeType();

			if (!selectedMimeType.current) {
				console.warn("Kein unterstütztes Audioformat für Voice gefunden.");
			}

			if (socket.current) {
				socket.current.close();
				socket.current = null;
			}

			setRoom(resolvedRoom);
			setUserName(resolvedUserName);

			const newSocket = new WebSocket(url);
			newSocket.binaryType = "arraybuffer";
			socket.current = newSocket;

			newSocket.addEventListener("open", () => {
				newSocket.send(
					JSON.stringify({
						type: "join",
						room: resolvedRoom,
						username: resolvedUserName,
					})
				);

				setConnectedState(true);
			});

			newSocket.addEventListener("message", event => {
				if (typeof event.data === "string") {
					// console.log("Voice text message:", event.data);
					handleTextMessage(event.data);
					return;
				}

				if (event.data instanceof ArrayBuffer) {
					// console.log(
					// 	"Voice binary message:",
					// 	event.data.byteLength,
					// 	"lastSpeaking:",
					// 	lastSpeakingUsername.current
					// );

					appendRemoteAudioChunk(lastSpeakingUsername.current, event.data);
					return;
				}

				const reader = new FileReader();

				reader.addEventListener("load", () => {
					if (reader.result instanceof ArrayBuffer) {
						console.log(
							"Voice blob message:",
							reader.result.byteLength,
							"lastSpeaking:",
							lastSpeakingUsername.current
						);

						appendRemoteAudioChunk(lastSpeakingUsername.current, reader.result);
					}
				});

				reader.readAsArrayBuffer(event.data);
			});

			newSocket.addEventListener("close", () => {
				cleanupRecording();
				cleanupRemoteUsers();
				setConnectedState(false);
				socket.current = null;
				currentUsername.current = "";
			});

			newSocket.addEventListener("error", error => {
				console.error("Voice-WebSocket-Fehler:", error);
			});
		},
		[
			appendRemoteAudioChunk,
			cleanupRecording,
			cleanupRemoteUsers,
			handleTextMessage,
			room,
			user.name,
		]
	);

	const startMicrophone = useCallback(async () => {
		if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
			console.warn("VoiceSocket ist nicht verbunden.");
			return;
		}

		const stream = await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
			video: false,
		});

		localStream.current = stream;

		startMicrophonePowerIndicator(stream);

		if (!selectedMimeType.current) {
			selectedMimeType.current = getSupportedMimeType();
		}

		if (!selectedMimeType.current) {
			console.warn("Kein unterstütztes MediaRecorder-Audioformat gefunden.");
			cleanupRecording();
			return;
		}

		const recorder = new MediaRecorder(stream, {
			mimeType: selectedMimeType.current,
			audioBitsPerSecond: 32000,
		});

		mediaRecorder.current = recorder;

		recorder.addEventListener("dataavailable", async event => {
			if (
				event.data.size > 0 &&
				socket.current &&
				socket.current.readyState === WebSocket.OPEN &&
				socket.current.bufferedAmount < 256 * 1024
			) {
				socket.current.send(
					JSON.stringify({
						type: "power",
						username: currentUsername.current || user.name,
						volume: microphoneMuted ? 0 : microphoneVolumeRef.current,
					})
				);

				const buffer = await event.data.arrayBuffer();
				socket.current.send(buffer);
			}
		});

		recorder.addEventListener("start", () => {
			setMicrophoneActive(true);
			setMicrophoneMuted(false);
		});

		recorder.addEventListener("stop", () => {
			setMicrophoneActive(false);
		});

		recorder.start(250);
	}, [cleanupRecording, startMicrophonePowerIndicator]);

	const stopMicrophone = useCallback(() => {
		cleanupRecording();
	}, [cleanupRecording]);

	const muteMicrophone = useCallback(() => {
		if (!localStream.current) {
			return;
		}

		for (const track of localStream.current.getAudioTracks()) {
			track.enabled = false;
		}

		setMicrophoneMuted(true);
		setMicrophoneVolume(0);
	}, []);

	const unmuteMicrophone = useCallback(() => {
		if (!localStream.current) {
			return;
		}

		for (const track of localStream.current.getAudioTracks()) {
			track.enabled = true;
		}

		setMicrophoneMuted(false);
	}, []);

	const toggleMicrophoneMute = useCallback(() => {
		if (microphoneMuted) {
			unmuteMicrophone();
			return;
		}

		muteMicrophone();
	}, [microphoneMuted, muteMicrophone, unmuteMicrophone]);

	const setUserVolume = useCallback(
		(username: string, volume: number) => {
			const normalizedVolume = Math.max(0, Math.min(100, volume));
			const user = remoteUsers.current.get(username);

			if (user) {
				user.volume = normalizedVolume;

				if (user.audio) {
					user.audio.volume = normalizedVolume;
				}
			}

			updateUserState(username, {
				volume: normalizedVolume,
			});
		},
		[updateUserState]
	);

	useEffect(() => {
		return () => {
			disconnect();

			if (speakingTimeout.current) {
				clearTimeout(speakingTimeout.current);
			}
		};
	}, [disconnect]);

	return (
		<VoiceSocketServerContext.Provider
			value={
				{
					connected: connectedState,
					microphoneActive,
					microphoneMuted,
					microphoneVolume,
					speakingUsername,
					users,
					userName: user.name,
					room,
					setUserName,
					setRoom,
					Actions: {
						connect,
						disconnect,
						startMicrophone,
						stopMicrophone,
						muteMicrophone,
						unmuteMicrophone,
						toggleMicrophoneMute,
						setUserVolume,
					},
				} as iVoiceSocketServerContextType
			}
		>
			{children}
		</VoiceSocketServerContext.Provider>
	);
};

export default () => useContext(VoiceSocketServerContext);